import { useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2, ChevronLeft, ChevronRight, CheckCircle,
  Camera, ClipboardCheck, CalendarClock, CalendarCheck,
} from "lucide-react";
import { formatCurrency, MONTHS } from "@/lib/config";
import { StatutDepotBadge, StatutDossierBadge } from './DashboardHelpers';
import {
  MarquerChantierCompleteDialog,
  useMarquerChantierComplete,
} from './MarquerChantierCompleteDialog';

const MOIS_COURT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const API_URL = process.env.REACT_APP_BACKEND_URL;

// 🆕 [Phase v13.51 — 2026-07-14] Sujet 4.E — Helper libellé FR-CA pour la portion approx
const MOIS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
const PORTIONS_FR = { debut: "Début", milieu: "Milieu", fin: "Fin" };

function libelleVoeuApprox(voeu) {
  if (!voeu) return "Planification approximative";
  const portion = PORTIONS_FR[voeu.portion] || voeu.portion || "";
  const mois = MOIS_FR[(voeu.mois || 1) - 1] || "";
  const n = voeu.duree_jours_ouvrables || 0;
  const s = n > 1 ? "s" : "";
  return `${portion} ${mois} ${voeu.annee || ""} — ${n} jour${s} ouvrable${s}`;
}

export function SectionProductionAvenir({
  deptView, year, moisProd, setMoisProd, loadingProd,
  filteredDossiersProd, filteredDossiersProdEnriched,
  canDo, navigate,
  onReloadRequested,
  userRole,
}) {
  // 🆕 [Phase v13.51 — 2026-07-14] Sujet 4.E — Hook pour marquer chantier complété
  const { dialogState, loading: markLoading, marquerComplet, retirerQuandMeme, closeDialog, retourAuBT } =
    useMarquerChantierComplete({
      apiUrl: API_URL,
      axios,
      onSuccess: () => onReloadRequested?.(),
      onNavigate: navigate,
      toast,
    });

  // 🆕 [Phase v13.51 — 2026-07-14] Tri chronologique croissant (plus proche EN HAUT)
  // Basé sur date_debut du dossier (déjà normalisée backend).
  const dossiersEnrichisTries = useMemo(() => {
    const arr = [...(filteredDossiersProdEnriched || [])];
    arr.sort((a, b) => {
      const da = a.date_debut || a.dates_approx_ce_mois?.[0] || "9999";
      const db_ = b.date_debut || b.dates_approx_ce_mois?.[0] || "9999";
      return da.localeCompare(db_);
    });
    return arr;
  }, [filteredDossiersProdEnriched]);

  // 🆕 Permission pour bouton "Marquer chantier complété" — Owner ou CP
  const peutMarquerComplet = userRole === "proprietaire" || userRole === "charge_projet";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600" />
            Production à venir {deptView !== "global" ? `${deptView} ` : ""}— {MONTHS[moisProd]} {year}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setMoisProd(m => m === 1 ? 12 : m - 1)}
              data-testid="btn-prod-mois-precedent">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-sm font-medium px-2">{MOIS_COURT[moisProd - 1]}</span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setMoisProd(m => m === 12 ? 1 : m + 1)}
              data-testid="btn-prod-mois-suivant">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {canDo('production_voir_details_cp')
            ? `${dossiersEnrichisTries.length} projet(s) planifie(s) au calendrier`
            : `${filteredDossiersProd.length} projet(s) planifie(s) au calendrier`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingProd ? (
          <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
        ) : (canDo('production_voir_details_cp') ? dossiersEnrichisTries : filteredDossiersProd).length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4 text-center">Aucun chantier planifie pour {MOIS_COURT[moisProd - 1]} {year}</p>
        ) : canDo('production_voir_details_cp') ? (
          <div className="space-y-2" data-testid="production-enriched">
            {dossiersEnrichisTries.map(d => {
              const today = new Date();
              const debut = d.date_debut ? new Date(d.date_debut + 'T12:00:00') : null;
              const daysUntil = debut ? Math.ceil((debut - today) / 86400000) : null;
              const daysLabel = daysUntil !== null ? (daysUntil <= 0 ? 'En cours' : daysUntil === 1 ? 'Demain' : `Dans ${daysUntil}j`) : '';
              const isEnProd = d.statut === 'en_production';
              const isComplete = d.bt_complet;
              // 🆕 [Phase v13.51 — 2026-07-14] Nouveaux flags v13.51
              const isCompleteV2 = d.bt_complet_v2;
              const isRetirable = d.est_retirable;
              const inspectionOk = d.inspection_reussie;
              const photosOk = (d.nb_photos_apres || 0) >= 1;
              const typePlanif = d.type_planification || "officielle"; // officielle | approximative | mixte
              const isApprox = typePlanif === "approximative";
              const isMixte = typePlanif === "mixte";
              const statutLabel = isEnProd ? 'En production' : (isApprox ? 'Planif approx' : 'Planifié');
              const statutColor = isEnProd
                ? 'bg-blue-100 text-blue-700'
                : (isApprox ? 'bg-amber-100 text-amber-700' : 'bg-amber-100 text-amber-700');
              const borderColor = isApprox
                ? 'border-l-amber-400 bg-amber-50/30 border-dashed'
                : (isComplete ? 'border-l-emerald-500 bg-emerald-50/30' : 'border-l-red-500 bg-red-50/20');

              return (
                <div key={d.id}
                  data-testid={`prod-avenir-ligne-${d.id}`}
                  data-type-planification={typePlanif}
                  className={`p-3 border-l-4 rounded-lg text-sm space-y-1.5 hover:shadow-md transition-all ${borderColor} ${isApprox ? 'opacity-90' : ''}`}>
                  <div
                    className="flex items-center justify-between gap-2 cursor-pointer"
                    onClick={() => navigate(`/crm/projets/${d.dossier_id || d.id}`)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-semibold truncate text-slate-800">{d.client_nom}</p>
                      <Badge className={`text-[10px] shrink-0 ${statutColor}`}>{statutLabel}</Badge>
                      {isApprox && (
                        <Badge className="text-[10px] shrink-0 bg-amber-100 text-amber-700 border border-amber-300"
                          data-testid={`badge-planif-approx-${d.id}`}>
                          <CalendarClock className="w-3 h-3 inline mr-0.5" /> Planification approximative
                        </Badge>
                      )}
                      {!isApprox && (
                        <Badge className="text-[10px] shrink-0 bg-emerald-100 text-emerald-700"
                          data-testid={`badge-planif-officielle-${d.id}`}>
                          <CalendarCheck className="w-3 h-3 inline mr-0.5" /> Dates officielles
                        </Badge>
                      )}
                    </div>
                    <span className={`text-xs font-bold shrink-0 ${daysUntil !== null && daysUntil <= 3 ? 'text-red-600' : daysUntil !== null && daysUntil <= 7 ? 'text-amber-600' : 'text-slate-500'}`}>
                      {daysLabel}
                    </span>
                  </div>

                  {/* 🆕 [Phase v13.51] Note "Planif approx en DB" si le dossier a AUSSI des approx */}
                  {isMixte && (
                    <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
                      <CalendarClock className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-800 italic" data-testid={`note-approx-en-db-${d.id}`}>
                        Planification approximative également enregistrée en base de données pour ce projet
                      </p>
                    </div>
                  )}

                  {/* Voeu approx (portion / durée / chef) — approx pur ou mixte */}
                  {(isApprox || isMixte) && d.planif_approx_voeu && (
                    <div className="text-[11px] text-amber-700 italic pl-1" data-testid={`voeu-approx-${d.id}`}>
                      🔮 {libelleVoeuApprox(d.planif_approx_voeu)}
                    </div>
                  )}

                  {/* ⚠ Notes importantes */}
                  {d.notes_importantes && (
                    <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                      <span className="text-amber-600 text-xs font-bold shrink-0 mt-0.5">⚠</span>
                      <p className="text-xs text-amber-800 font-medium leading-snug">{d.notes_importantes}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                    {d.date_debut && !isApprox && (
                      <span>{d.date_debut}{d.date_fin && d.date_fin !== d.date_debut ? ` → ${d.date_fin}` : ''} ({d.nb_jours_ce_mois || d.nb_jours_production}j ce mois)</span>
                    )}
                    {isApprox && d.dates_approx_ce_mois?.length > 0 && (
                      <span>Voeu : {d.dates_approx_ce_mois[0]}{d.dates_approx_ce_mois.length > 1 ? ` → ${d.dates_approx_ce_mois[d.dates_approx_ce_mois.length - 1]}` : ''} ({d.dates_approx_ce_mois.length}j)</span>
                    )}
                    {!isApprox && (d.heures_budgetees !== undefined) && (
                      <span className="font-medium">{d.heures_restantes}h restantes / {d.heures_budgetees}h</span>
                    )}
                  </div>

                  {/* Badges d'état — masqués pour les approx purs (pas encore de données) */}
                  {!isApprox && (
                    <div className="flex flex-wrap gap-1.5">
                      {d.nb_commandes > 0 ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{d.nb_commandes} cmd</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-red-100 text-red-600">0 cmd</Badge>
                      )}
                      {d.type_travaux !== 'interieur' && d.lavage_status === 'a_faire' && (
                        <Badge className="text-[10px] bg-red-100 text-red-700">Lavage a ceduler</Badge>
                      )}
                      {d.type_travaux !== 'interieur' && d.lavage_status === 'cedule' && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">Lavage cedule</Badge>
                      )}
                      {d.type_travaux !== 'interieur' && d.lavage_status === 'complete' && (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Lavage fait</Badge>
                      )}
                      {d.type_travaux !== 'interieur' && d.has_equipement && (
                        d.elevation_terminee ? (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Elevation terminee</Badge>
                        ) : (
                          <Badge className="text-[10px] bg-blue-100 text-blue-700">Elevation cedulee</Badge>
                        )
                      )}
                      {d.etapes_total > 0 && (
                        <Badge className={`text-[10px] ${d.etapes_completees === d.etapes_total ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {d.etapes_completees}/{d.etapes_total} etapes
                        </Badge>
                      )}
                      {d.has_service_call && (
                        <Badge className="text-[10px] bg-orange-100 text-orange-700">Service Call</Badge>
                      )}
                      {/* 🆕 [Phase v13.51] Nouveaux badges Inspection + Photos */}
                      <Badge
                        className={`text-[10px] ${inspectionOk ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                        data-testid={`badge-inspection-${d.id}`}
                      >
                        <ClipboardCheck className="w-3 h-3 inline mr-0.5" />
                        {inspectionOk ? 'Inspection ✓' : 'Inspection ✗'}
                      </Badge>
                      <Badge
                        className={`text-[10px] ${photosOk ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                        data-testid={`badge-photos-${d.id}`}
                      >
                        <Camera className="w-3 h-3 inline mr-0.5" />
                        {photosOk ? `${d.nb_photos_apres} photo${d.nb_photos_apres > 1 ? 's' : ''}` : 'Photos ✗'}
                      </Badge>

                      {isCompleteV2 ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700" data-testid={`bt-complet-v2-${d.id}`}>
                          <CheckCircle className="w-3 h-3 inline mr-0.5" /> Prêt à retirer
                        </Badge>
                      ) : isComplete ? (
                        <Badge className="text-[10px] bg-blue-100 text-blue-700" data-testid={`bt-complet-enriched-${d.id}`}>BT complet</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-red-100 text-red-600">BT incomplet</Badge>
                      )}
                    </div>
                  )}

                  {/* 🆕 [Phase v13.51] Bouton "Marquer chantier complété" — Owner/CP only, masqué pour approx purs */}
                  {peutMarquerComplet && !isApprox && (
                    <div className="flex justify-end pt-1">
                      <Button
                        variant={isRetirable ? "default" : "outline"}
                        size="sm"
                        className={`h-7 text-xs ${isRetirable ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-amber-300 text-amber-800 hover:bg-amber-50'}`}
                        onClick={(e) => { e.stopPropagation(); marquerComplet(d); }}
                        disabled={markLoading}
                        data-testid={`btn-marquer-complet-${d.id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {markLoading ? 'Traitement…' : 'Marquer chantier complété'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredDossiersProd.map(d => {
              const s = d.sections_status || {};
              const isExterieur = d.departement === 'PRQC' && d.type_travaux !== 'interieur';
              const btComplet = s.produits && s.codes_couleur && s.dates_chantier && s.equipe && s.lavage && s.email_presentation && s.commande_initiale && (!isExterieur || (s.zones && s.etapes));
              return (
              <div key={d.id} onClick={() => navigate(`/crm/projets/${d.dossier_id || d.id}`)} className="flex flex-col gap-1.5 p-2.5 border rounded-lg bg-gray-50/50 text-sm hover:bg-blue-50 hover:border-blue-200 transition-colors cursor-pointer">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium truncate text-blue-700">{d.nom_dossier || d.client_nom}</p>
                      <StatutDossierBadge statut={d.statut} />
                      {btComplet ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700" data-testid={`bt-complet-badge-${d.id}`}>BT complet</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-red-100 text-red-600" data-testid={`bt-incomplet-badge-${d.id}`}>BT incomplet</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{d.adresse_travaux}, {d.ville_travaux}</p>
                  </div>
                  {canDo('production_voir_finances') && (
                    <div className="text-right shrink-0">
                      <p className="text-xs font-semibold text-gray-700">{formatCurrency(d.montant_soumission)}</p>
                      <StatutDepotBadge depot={d.depot} />
                    </div>
                  )}
                </div>
                {/* ⚠ Notes importantes */}
                {d.notes_importantes && (
                  <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                    <span className="text-amber-600 text-xs font-bold shrink-0 mt-0.5">⚠</span>
                    <p className="text-xs text-amber-800 font-medium leading-snug">{d.notes_importantes}</p>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* 🆕 [Phase v13.51 — 2026-07-14] Dialog sections incomplètes */}
      <MarquerChantierCompleteDialog
        open={dialogState.open}
        onOpenChange={(o) => { if (!o) closeDialog(); }}
        sectionsManquantes={dialogState.sectionsManquantes}
        clientNom={dialogState.clientNom}
        onRetourAuBT={retourAuBT}
        onRetirerQuandMeme={retirerQuandMeme}
        loading={markLoading}
      />
    </Card>
  );
}
